// =============================================================================
// usePresence
// =============================================================================
// PURPOSE: Track who's online in real-time, SCOPED PER ORGANIZATION.
//
// ARCHITECTURE:
// - One presence channel per org: "presence:online:<organization_id>"
// - Each user PUBLISHES (track) only to their HOME org channel → appears once,
//   in the org they're currently working in.
// - Each user SUBSCRIBES to every channel in `visibleOrgIds` and merges the
//   rosters. For a rep, visibleOrgIds = [their org] → they only ever receive
//   their own org's presence over the wire (real isolation, not UI filtering).
//   For a super-admin, visibleOrgIds = all orgs → merged god view.
// - Broadcasts name, role, current page, AND organizationId/orgName so a
//   cross-org (god) view can label each person by org.
// - Updates page location on route changes (hashchange), re-tracks home channel.
// - Per-channel exponential backoff reconnection on CHANNEL_ERROR.
// - Auto-cleans up every channel on unmount.
//
// WHY per-org channels (not one global channel + client-side filter):
//   A single global channel streams every org's names/roles/pages to every
//   browser; the UI could filter it but the data is still on the wire (visible
//   in devtools) — a cross-tenant leak. Per-org channels put isolation on the
//   WIRE. See recon 2026-07-09.
//
// OPTION C (private channels): when `usePrivateChannels` is true the channels
//   are created with `private: true`, and Supabase Realtime Authorization RLS
//   on `realtime.messages` enforces org-scoping at the DB layer (the channel
//   name alone is no longer the boundary). The client auto-forwards the user's
//   JWT to realtime (supabase-js `_listenForAuthEvents` → realtime.setAuth), so
//   no extra wiring is needed here — flip the flag once the migration is live:
//   sql/migrations/2026-07-09_presence_channel_authorization.sql
//
// KEY DECISIONS:
// - name/role/homeOrgName stored in refs (not effect deps) to prevent channel
//   teardown during initial auth load when realUser fields update rapidly.
// - Reconnect only when userId / homeOrgId / the SET of visibleOrgIds /
//   usePrivateChannels / enabled change (visibleOrgIds compared by joined key).
// - onlineSince preserved across page navigations (set once per session).
// - Aggregate `status` reflects the HOME channel (the one the user publishes to);
//   a read-only cross-org channel erroring must not flip the whole widget red.
//
// SECURITY:
// - Only broadcasts name, role, current path, org id/name — no sensitive data.
// - Presence state is ephemeral (never persisted to the database).
// =============================================================================

import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { RealtimeChannel } from "@supabase/supabase-js";

export interface PresenceUser {
  userId: string;
  name: string;
  role: string;
  page: string;
  organizationId: string;
  orgName: string;
  onlineSince: string; // ISO timestamp
}

interface UsePresenceConfig {
  userId: string;
  name: string;
  role: string;
  /** Org the user publishes their own presence to (their active/current org). */
  homeOrgId: string | null;
  /** Display name of the home org (for the broadcast payload). */
  homeOrgName?: string | null;
  /** Orgs to subscribe to and merge. Reps = [homeOrgId]; super-admins = all. */
  visibleOrgIds: string[];
  /** Option C: create channels as `private: true` (RLS-enforced). Default off. */
  usePrivateChannels?: boolean;
  enabled?: boolean;
}

export type PresenceStatus = "disconnected" | "connecting" | "connected" | "error";

const MAX_RECONNECT_ATTEMPTS = 3;
const CHANNEL_PREFIX = "presence:online:";

export function usePresence(config: UsePresenceConfig) {
  const {
    userId,
    name,
    role,
    homeOrgId,
    homeOrgName,
    visibleOrgIds,
    usePrivateChannels = false,
    enabled = true,
  } = config;

  // Blast-radius guard: this hook runs in the Sidebar on EVERY page, so any
  // throw here (a spread of a non-array) takes down the whole app behind the
  // error boundary. A caller passing undefined/null — or a stale HMR module
  // mid-edit where the prop shape hasn't caught up — must degrade to "no
  // presence", never crash the dashboard. Coerce once; iterate this everywhere.
  const safeVisibleOrgIds = Array.isArray(visibleOrgIds) ? visibleOrgIds : [];

  const [onlineUsers, setOnlineUsers] = useState<PresenceUser[]>([]);
  const [status, setStatus] = useState<PresenceStatus>("disconnected");

  // One channel per orgId, plus per-channel bookkeeping.
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  // Latest roster per org — merged into onlineUsers on any channel sync.
  const rosterByOrgRef = useRef<Map<string, PresenceUser[]>>(new Map());
  // Per-channel reconnect attempts + pending timers.
  const reconnectAttemptsRef = useRef<Map<string, number>>(new Map());
  const reconnectTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const isUnmountedRef = useRef(false);

  // Values that change during auth init but must NOT re-create channels.
  const nameRef = useRef(name);
  const roleRef = useRef(role);
  const homeOrgNameRef = useRef(homeOrgName);
  useEffect(() => {
    nameRef.current = name;
  }, [name]);
  useEffect(() => {
    roleRef.current = role;
  }, [role]);
  useEffect(() => {
    homeOrgNameRef.current = homeOrgName;
  }, [homeOrgName]);

  // Preserve onlineSince across page navigations — set once per session.
  const onlineSinceRef = useRef(new Date().toISOString());

  const getCurrentPage = useCallback(() => {
    const hash = window.location.hash;
    return hash.replace(/^#\/?/, "/") || "/";
  }, []);

  // Recompute the merged roster from every org bucket. A user can legitimately
  // appear in more than one bucket (a multi-org session with two tabs, or the
  // transient window mid org-switch), so collapse to one row per userId
  // DETERMINISTICALLY — keep the most recent session (latest onlineSince)
  // rather than whichever channel happened to sync first (Map insertion order).
  const recomputeOnlineUsers = useCallback(() => {
    if (isUnmountedRef.current) return;
    const byUser = new Map<string, PresenceUser>();
    for (const users of rosterByOrgRef.current.values()) {
      for (const u of users) {
        const prev = byUser.get(u.userId);
        // onlineSince is an ISO string → lexicographic compare == chronological.
        if (!prev || u.onlineSince > prev.onlineSince) byUser.set(u.userId, u);
      }
    }
    setOnlineUsers(Array.from(byUser.values()));
  }, []);

  // The current track() payload for THIS user on their home channel.
  const buildTrackPayload = useCallback(
    (): PresenceUser => ({
      userId,
      name: nameRef.current,
      role: roleRef.current,
      page: getCurrentPage(),
      organizationId: homeOrgId || "",
      orgName: homeOrgNameRef.current || "",
      onlineSince: onlineSinceRef.current,
    }),
    [userId, homeOrgId, getCurrentPage]
  );

  // Connect (or reconnect) a single org channel. Stored in a ref so the
  // backoff timer can call the latest version without re-subscribing effects.
  const connectChannelRef = useRef<(orgId: string) => void>(() => {});
  const connectChannel = useCallback(
    (orgId: string) => {
      if (isUnmountedRef.current || !supabase || !userId || !orgId) return;

      // Clear any pending reconnect for this org.
      const pending = reconnectTimersRef.current.get(orgId);
      if (pending) {
        clearTimeout(pending);
        reconnectTimersRef.current.delete(orgId);
      }

      // Tear down an existing channel for this org before recreating.
      const existing = channelsRef.current.get(orgId);
      if (existing) {
        supabase.removeChannel(existing);
        channelsRef.current.delete(orgId);
      }

      const isHome = orgId === homeOrgId;

      const channel = supabase.channel(`${CHANNEL_PREFIX}${orgId}`, {
        config: { presence: { key: userId }, private: usePrivateChannels },
      });

      channel
        .on("presence", { event: "sync" }, () => {
          if (isUnmountedRef.current) return;
          const state = channel.presenceState<PresenceUser>();
          const users: PresenceUser[] = [];
          for (const [, presences] of Object.entries(state)) {
            if (presences.length > 0) {
              users.push(presences[presences.length - 1] as PresenceUser);
            }
          }
          rosterByOrgRef.current.set(orgId, users);
          recomputeOnlineUsers();
        })
        .subscribe(async (subStatus) => {
          if (isUnmountedRef.current) return;

          if (subStatus === "SUBSCRIBED") {
            reconnectAttemptsRef.current.set(orgId, 0);
            // Only the home channel gets a live status; read-only cross-org
            // channels must not flip the aggregate widget status.
            if (isHome) setStatus("connected");
            // Publish self ONLY to the home org channel.
            if (isHome && homeOrgId) {
              await channel.track(buildTrackPayload());
            }
          } else if (subStatus === "CHANNEL_ERROR" || subStatus === "TIMED_OUT") {
            if (isHome) setStatus("error");

            const attempts = reconnectAttemptsRef.current.get(orgId) || 0;
            if (!isUnmountedRef.current && attempts < MAX_RECONNECT_ATTEMPTS) {
              const next = attempts + 1;
              reconnectAttemptsRef.current.set(orgId, next);
              const delay = Math.min(1000 * Math.pow(2, next), 10000);
              const timer = setTimeout(() => {
                if (!isUnmountedRef.current) connectChannelRef.current(orgId);
              }, delay);
              reconnectTimersRef.current.set(orgId, timer);
            } else if (channelsRef.current.get(orgId) === channel) {
              // Retries exhausted — drop this org's last-known roster so stale
              // "ghost" presences don't keep merging into the view. Guard: only
              // if THIS channel is still the registered one, so a superseded
              // channel's late callback can't wipe a freshly-reconnected org.
              rosterByOrgRef.current.delete(orgId);
              recomputeOnlineUsers();
            }
          } else if (subStatus === "CLOSED") {
            if (isHome) setStatus("disconnected");
            // Unexpected close (network drop — NOT our unmount teardown, which
            // clears the whole map): drop the roster so it doesn't linger.
            // Same superseded-channel guard as the give-up path above.
            if (!isUnmountedRef.current && channelsRef.current.get(orgId) === channel) {
              rosterByOrgRef.current.delete(orgId);
              recomputeOnlineUsers();
            }
          }
        });

      channelsRef.current.set(orgId, channel);
    },
    [userId, homeOrgId, usePrivateChannels, buildTrackPayload, recomputeOnlineUsers]
  );
  useEffect(() => {
    connectChannelRef.current = connectChannel;
  }, [connectChannel]);

  // Stable dependency key for the SET of visible orgs (order-independent).
  const visibleKey = [...safeVisibleOrgIds].filter(Boolean).sort().join(",");

  useEffect(() => {
    if (!enabled || !supabase || !userId || !homeOrgId) return;

    isUnmountedRef.current = false;
    // NOTE: onlineSince is intentionally NOT reset here — it's set once per
    // session by the ref initializer. Resetting on every effect re-run would
    // make "online since" snap back to now on an org switch (homeOrgId change),
    // even though the user never went offline. (Review finding, 2026-07-09.)
    setStatus("connecting");

    // The set of orgs to subscribe to: always include home (so the user can
    // publish + see their own org even if visibleOrgIds hasn't populated yet).
    const orgIds = Array.from(new Set([homeOrgId, ...safeVisibleOrgIds.filter(Boolean)]));

    // Defer connect to avoid synchronous setState inside the effect.
    queueMicrotask(() => {
      if (isUnmountedRef.current) return;
      for (const orgId of orgIds) connectChannel(orgId);
    });

    // On route change, re-track the HOME channel with the new page.
    const handleRouteChange = () => {
      if (isUnmountedRef.current) return;
      const home = channelsRef.current.get(homeOrgId);
      if (home) home.track(buildTrackPayload());
    };
    window.addEventListener("hashchange", handleRouteChange);

    // Snapshot refs for cleanup (avoids stale-ref lint + mid-teardown races).
    const channels = channelsRef.current;
    const rosterByOrg = rosterByOrgRef.current;
    const reconnectTimers = reconnectTimersRef.current;
    const reconnectAttempts = reconnectAttemptsRef.current;

    return () => {
      isUnmountedRef.current = true;
      window.removeEventListener("hashchange", handleRouteChange);
      for (const timer of reconnectTimers.values()) clearTimeout(timer);
      reconnectTimers.clear();
      reconnectAttempts.clear();
      for (const channel of channels.values()) supabase?.removeChannel(channel);
      channels.clear();
      rosterByOrg.clear();
    };
    // Reconnect only when identity / home / the SET of visible orgs / privacy /
    // enabled change — NOT name/role/homeOrgName (those are refs).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, homeOrgId, visibleKey, usePrivateChannels, enabled]);

  return { onlineUsers, status };
}
